
import React, { useRef, useEffect, useState } from 'react';
import type { Toast } from '../contexts/ToastContext';

// ─── ICONS ────────────────────────────────────────────────────────────────────

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 8.5L6.5 12L13 4" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ErrorIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 4L12 12M12 4L4 12" stroke="#DC2626" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="6" stroke="#FAF9F6" strokeWidth="1.5"/>
    <path d="M8 7V11" stroke="#FAF9F6" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="8" cy="5" r="0.75" fill="#FAF9F6"/>
  </svg>
);

const WarningIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 2L14.5 13.5H1.5L8 2Z" stroke="#F59E0B" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M8 6.5V9.5" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="8" cy="11.5" r="0.75" fill="#F59E0B"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.5 2.5L9.5 9.5M9.5 2.5L2.5 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

// ─── CONFIG ───────────────────────────────────────────────────────────────────

const ACCENT_COLORS: Record<Toast['type'], string> = {
  success: '#D4AF37',  // gold
  error: '#DC2626',    // red
  info: '#FAF9F6',     // ivory
  warning: '#F59E0B',  // amber
};

const ICONS: Record<Toast['type'], React.FC> = {
  success: CheckIcon,
  error: ErrorIcon,
  info: InfoIcon,
  warning: WarningIcon,
};

// ─── SINGLE TOAST ITEM ───────────────────────────────────────────────────────

interface ToastItemProps {
  toast: Toast;
  onDismiss: (id: string) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onDismiss }) => {
  const [isExiting, setIsExiting] = useState(false);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const touchDeltaRef = useRef(0);
  const itemRef = useRef<HTMLDivElement>(null);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => onDismiss(toast.id), 280);
  };

  // Swipe-to-dismiss for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
    touchDeltaRef.current = 0;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStartRef.current || !itemRef.current) return;
    const deltaX = e.touches[0].clientX - touchStartRef.current.x;
    const deltaY = Math.abs(e.touches[0].clientY - touchStartRef.current.y);

    // Only handle horizontal swipe
    if (deltaY > 30) {
      touchStartRef.current = null;
      return;
    }

    touchDeltaRef.current = deltaX;
    itemRef.current.style.transform = `translateX(${deltaX}px)`;
    itemRef.current.style.opacity = `${1 - Math.min(Math.abs(deltaX) / 200, 0.6)}`;
  };

  const handleTouchEnd = () => {
    if (!itemRef.current) return;
    if (Math.abs(touchDeltaRef.current) > 80) {
      // Swipe far enough to dismiss
      const direction = touchDeltaRef.current > 0 ? '100%' : '-100%';
      itemRef.current.style.transform = `translateX(${direction})`;
      itemRef.current.style.opacity = '0';
      setTimeout(() => onDismiss(toast.id), 200);
    } else {
      // Snap back
      itemRef.current.style.transform = 'translateX(0)';
      itemRef.current.style.opacity = '1';
    }
    touchStartRef.current = null;
    touchDeltaRef.current = 0;
  };

  const Icon = ICONS[toast.type];
  const accentColor = ACCENT_COLORS[toast.type];

  return (
    <div
      ref={itemRef}
      role="alert"
      aria-live="polite"
      className={`toast-item ${isExiting ? 'toast-exit' : 'toast-enter'}`}
      style={{
        display: 'flex',
        alignItems: 'stretch',
        background: 'rgba(18, 18, 18, 0.82)',
        backdropFilter: 'blur(24px) saturate(1.5)',
        WebkitBackdropFilter: 'blur(24px) saturate(1.5)',
        borderRadius: '16px',
        borderTop: '1px solid rgba(255, 255, 255, 0.15)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.06)',
        overflow: 'hidden',
        minWidth: '280px',
        maxWidth: '420px',
        width: 'auto',
        transition: 'transform 0.2s ease, opacity 0.2s ease',
        willChange: 'transform, opacity',
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Left accent bar */}
      <div
        style={{
          width: '3px',
          minHeight: '100%',
          background: accentColor,
          flexShrink: 0,
        }}
      />

      {/* Content */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', flex: 1, minWidth: 0 }}>
        {/* Icon */}
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon />
        </div>

        {/* Message */}
        <p
          style={{
            fontFamily: '"Inter", sans-serif',
            fontSize: '13px',
            lineHeight: '1.4',
            color: '#FAF9F6',
            margin: 0,
            flex: 1,
            minWidth: 0,
          }}
        >
          {toast.message}
        </p>

        {/* Action button */}
        {toast.action && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toast.action!.onClick();
              handleDismiss();
            }}
            style={{
              fontFamily: '"Inter", sans-serif',
              fontSize: '11px',
              fontWeight: 600,
              color: '#D4AF37',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              padding: '4px 8px',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              transition: 'color 0.2s',
            }}
            onMouseEnter={(e) => { (e.target as HTMLElement).style.color = '#FAF9F6'; }}
            onMouseLeave={(e) => { (e.target as HTMLElement).style.color = '#D4AF37'; }}
          >
            {toast.action.label}
          </button>
        )}

        {/* Close button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDismiss();
          }}
          aria-label="Dismiss notification"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'rgba(250, 249, 246, 0.6)',
            padding: '4px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'color 0.2s',
            minWidth: '44px',
            minHeight: '44px',
          }}
          onMouseEnter={(e) => { (e.target as HTMLElement).style.color = '#FAF9F6'; }}
          onMouseLeave={(e) => { (e.target as HTMLElement).style.color = 'rgba(250, 249, 246, 0.6)'; }}
        >
          <CloseIcon />
        </button>
      </div>
    </div>
  );
};

// ─── TOAST CONTAINER ──────────────────────────────────────────────────────────

interface ToastContainerProps {
  toasts: Toast[];
  onDismiss: (id: string) => void;
}

const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onDismiss }) => {
  if (toasts.length === 0) return null;

  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      className="no-print"
      style={{
        position: 'fixed',
        top: '12px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 10001,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        alignItems: 'center',
        pointerEvents: 'none',
        width: '100%',
        maxWidth: '440px',
        padding: '0 16px',
        boxSizing: 'border-box',
      }}
    >
      {toasts.map((toast) => (
        <div key={toast.id} style={{ pointerEvents: 'auto', width: '100%', display: 'flex', justifyContent: 'center' }}>
          <ToastItem toast={toast} onDismiss={onDismiss} />
        </div>
      ))}
    </div>
  );
};

export default ToastContainer;
